import { useEffect, useRef, useState } from 'react';

import type { ItineraryRoomGroup, RoomGroupInput } from '@utazzo/shared';

/**
 * Room / family planning model for the itinerary builder.
 *
 * Concept: travellers are grouped into FAMILIES, each family into ROOMS. The
 * common case is a single family holding everyone (no "family" chrome shown).
 * Multiple families exist only when separate groups need their own rooms +
 * separate billing later (split-payment quotation).
 *
 * Capacity rule: the lead declares the asked pax (adults/children/infants).
 * That seeds Family 1 and stays visible as a reference, but it is NOT a hard
 * ceiling - the agent has free hand to allocate more or fewer travellers.
 * `overCapacity` still flags a mismatch so the UI can surface it as a
 * heads-up, without blocking the edit or the save.
 *
 * The model is deliberately kept here (not in the component) so it is small,
 * explicit and unit-testable, and so the component stays a thin view.
 */

export interface PaxCount {
  adults: number;
  children: number;
  infants: number;
}
/** One child's age. `null` = not recorded yet (shown as an empty box, never a
 *  fabricated default). Legacy / frozen rooms carry all-null arrays. */
export type ChildAge = number | null;

export interface Room extends PaxCount {
  id: string;
  /** How many of `children` share existing bedding (no extra bed). Default 0. */
  childrenNoBed?: number;
  /** Per-child ages (declaration order); length tracks `children`. Autofilled
   *  from the lead when available, else entered by the agent. Entries are
   *  `null` until a real age is provided - we never show a fake default. */
  childAges?: ChildAge[];
}
export interface Family {
  id: string;
  /** Optional family name (e.g. a surname). Null/blank = unnamed. */
  label: string | null;
  rooms: Room[];
}

const PAX_FIELDS: (keyof PaxCount)[] = ['adults', 'children', 'infants'];

// Per-room input ceiling - a sanity bound only, not a business rule.
const ROOM_FIELD_MAX = 20;

const uid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const sumPax = (rooms: Room[]): PaxCount =>
  rooms.reduce<PaxCount>(
    (acc, r) => ({
      adults: acc.adults + (r.adults || 0),
      children: acc.children + (r.children || 0),
      infants: acc.infants + (r.infants || 0),
    }),
    { adults: 0, children: 0, infants: 0 },
  );

const makeRoom = (pax: Partial<PaxCount> = {}): Room => ({
  id: uid(),
  adults: pax.adults ?? 0,
  children: pax.children ?? 0,
  infants: pax.infants ?? 0,
  childAges: [],
});

/** Fallback age used ONLY at persistence time to give TBO a number when a child
 *  in an otherwise-filled room was left blank (child-with-bed midpoint). Never
 *  shown in the UI - empty boxes stay empty. */
export const DEFAULT_CHILD_AGE = 8;

/** Resize a room's `childAges` to match a new `children` count: keep existing
 *  ages (order preserved, nulls included), pad new slots with `null` (an empty
 *  box, never a fake default), drop the tail on a shrink. Returns the SAME
 *  array reference when nothing changes so `setRoom` patches stay cheap. */
export const resizeChildAges = (ages: ChildAge[] | undefined, children: number): ChildAge[] => {
  const cur = ages ?? [];
  if (cur.length === children) return cur;
  if (children <= 0) return [];
  if (children < cur.length) return cur.slice(0, children);
  return [...cur, ...Array.from({ length: children - cur.length }, () => null)];
};

/** Prepare a room's ages for persistence. When NOTHING was entered, save `[]`
 *  (genuinely "not recorded" - the search paths fall back to a default). When
 *  at least one age was entered, fill any remaining blanks with the default so
 *  TBO gets a number for every child in the room. */
export const finalizeChildAges = (ages: ChildAge[] | undefined, children: number): number[] => {
  const full = resizeChildAges(ages, children);
  if (full.every((a) => a === null || a === undefined)) return [];
  return full.map((a) => a ?? DEFAULT_CHILD_AGE);
};

const oneFamily = (pax: PaxCount): Family => ({ id: uid(), label: null, rooms: [makeRoom(pax)] });

export interface RoomPlan {
  families: Family[];
  capacity: PaxCount;
  assigned: PaxCount;
  /** capacity - assigned, per field (never below 0 in the UI thanks to caps). */
  remaining: PaxCount;
  allRooms: Room[];
  totalRooms: number;
  isMultiFamily: boolean;
  /** Any field allocated beyond the asked pax. Informational only. */
  overCapacity: boolean;
  /** Every asked seat has been placed in a room. */
  fullyAssigned: boolean;
  canAddRoom: boolean;
  canAddFamily: boolean;
  /** Sanity ceiling for a room's field input (not tied to the asked pax). */
  maxFor: (roomId: string, field: keyof PaxCount) => number;
  setRoom: (
    familyId: string,
    roomId: string,
    patch: Partial<PaxCount> & { childrenNoBed?: number; childAges?: ChildAge[] },
  ) => void;
  addRoom: (familyId: string) => void;
  removeRoom: (familyId: string, roomId: string) => void;
  addFamily: () => void;
  removeFamily: (familyId: string) => void;
  setFamilyLabel: (familyId: string, label: string) => void;
}

export function useRoomPlan(capacity: PaxCount, initial?: Family[]): RoomPlan {
  const hasInitial = Boolean(initial && initial.length > 0);
  const [families, setFamilies] = useState<Family[]>(() =>
    hasInitial ? (initial as Family[]) : [oneFamily(capacity)],
  );
  // While untouched, Family 1 mirrors the asked pax. Once the agent edits (or we
  // loaded a persisted split), we stop auto-seeding so it's never clobbered.
  const pristine = useRef(!hasInitial);
  // Track the last capacity we seeded against so a late-arriving lead count
  // (e.g. itinerary opened with adultsCount=0/1, then lead resolves to 2+)
  // can still top up a single default room that merely mirrored the old count.
  const prevCapacity = useRef(capacity);

  useEffect(() => {
    const prev = prevCapacity.current;
    prevCapacity.current = capacity;

    if (pristine.current) {
      setFamilies([oneFamily(capacity)]);
      return;
    }

    // Lead finished loading with a higher asked pax, and the only room still
    // exactly matches the previous capacity (i.e. the agent never hand-edited
    // the split). Top it up so we don't persist the stale undersized count.
    const grew =
      capacity.adults > prev.adults ||
      capacity.children > prev.children ||
      capacity.infants > prev.infants;
    if (!grew) return;

    setFamilies((fs) => {
      if (fs.length !== 1 || fs[0]!.rooms.length !== 1) return fs;
      const only = fs[0]!.rooms[0]!;
      if (
        only.adults === prev.adults &&
        only.children === prev.children &&
        only.infants === prev.infants
      ) {
        return [oneFamily(capacity)];
      }
      return fs;
    });
    // Re-seed only when the asked pax actually changes (lead finishes loading).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacity.adults, capacity.children, capacity.infants]);

  const edit = (fn: (fs: Family[]) => Family[]): void => {
    pristine.current = false;
    setFamilies(fn);
  };

  const allRooms = families.flatMap((f) => f.rooms);
  const assigned = sumPax(allRooms);
  const remaining: PaxCount = {
    adults: capacity.adults - assigned.adults,
    children: capacity.children - assigned.children,
    infants: capacity.infants - assigned.infants,
  };
  const overCapacity = PAX_FIELDS.some((f) => remaining[f] < 0);
  const fullyAssigned = PAX_FIELDS.every((f) => remaining[f] === 0);
  const canAddRoom = true;

  const maxFor = (): number => ROOM_FIELD_MAX;

  // A brand-new room/family gets up to 2 of the remaining adults so it isn't
  // born empty. When nothing is left, start at 0 - never invent an extra adult
  // (that used to push assigned above the lead's asked count, or leave a lone
  // "1 adult" room after the agent removed the fully-seeded first room).
  const seedRoom = (): Room => makeRoom({ adults: Math.min(2, Math.max(0, remaining.adults)) });

  return {
    families,
    capacity,
    assigned,
    remaining,
    allRooms,
    totalRooms: allRooms.length,
    isMultiFamily: families.length > 1,
    overCapacity,
    fullyAssigned,
    canAddRoom,
    canAddFamily: canAddRoom,
    maxFor,
    setRoom: (familyId, roomId, patch) =>
      edit((fs) =>
        fs.map((f) =>
          f.id === familyId
            ? {
                ...f,
                rooms: f.rooms.map((r) => {
                  if (r.id !== roomId) return r;
                  const merged = { ...r, ...patch };
                  // Keep the ages array in lockstep with the children count.
                  // An explicit `childAges` patch (an age edit) wins; otherwise
                  // a children-count change grows/shrinks the array.
                  merged.childAges =
                    patch.childAges ?? resizeChildAges(merged.childAges, merged.children);
                  return merged;
                }),
              }
            : f,
        ),
      ),
    addRoom: (familyId) =>
      edit((fs) =>
        fs.map((f) => (f.id === familyId ? { ...f, rooms: [...f.rooms, seedRoom()] } : f)),
      ),
    removeRoom: (familyId, roomId) =>
      edit((fs) =>
        fs.map((f) =>
          f.id === familyId ? { ...f, rooms: f.rooms.filter((r) => r.id !== roomId) } : f,
        ),
      ),
    addFamily: () => edit((fs) => [...fs, { id: uid(), label: null, rooms: [seedRoom()] }]),
    removeFamily: (familyId) => edit((fs) => fs.filter((f) => f.id !== familyId)),
    setFamilyLabel: (familyId, label) =>
      edit((fs) => fs.map((f) => (f.id === familyId ? { ...f, label } : f))),
  };
}

/** Persisted families -> editor state. Ages padded to the children count. */
export const familiesFromRoomGroups = (groups: readonly ItineraryRoomGroup[]): Family[] =>
  groups.map((g) => ({
    id: g.id,
    label: g.label ?? null,
    rooms: g.rooms.map((r) => ({
      id: r.id,
      adults: r.adults,
      children: r.children,
      infants: r.infants,
      childrenNoBed: r.childrenNoBed,
      childAges: resizeChildAges(r.childAges, r.children),
    })),
  }));

/**
 * A write-shape room plan (e.g. the shared FD default room plan) -> editor
 * state, with STABLE ids ('family-1', 'room-1', ...) so the value is
 * deterministic across renders (it is compared for dirtiness).
 */
export const familiesFromRoomGroupInputs = (groups: readonly RoomGroupInput[]): Family[] => {
  let roomNo = 0;
  return groups.map((g, gi) => ({
    id: `family-${gi + 1}`,
    label: g.label ?? null,
    rooms: g.rooms.map((r) => {
      roomNo += 1;
      return {
        id: `room-${roomNo}`,
        adults: r.adults,
        children: r.children,
        infants: r.infants,
        childrenNoBed: Math.min(r.childrenNoBed ?? 0, r.children),
        childAges: resizeChildAges(r.childAges, r.children),
      };
    }),
  }));
};

/** Editor state -> the write payload shared by the FIT plan save and FD config. */
export const roomGroupsPayload = (families: readonly Family[]): RoomGroupInput[] =>
  families.map((f) => ({
    label: f.label?.trim() ? f.label.trim() : null,
    rooms: f.rooms.map((r) => ({
      adults: r.adults,
      children: r.children,
      infants: r.infants,
      childrenNoBed: Math.min(r.childrenNoBed ?? 0, r.children),
      childAges: finalizeChildAges(r.childAges, r.children),
    })),
  }));

/**
 * Pricing does not depend on family names, so the quote-preview input drops
 * labels: renaming a family must not change the query key or refetch.
 */
export const previewRoomGroups = (groups: readonly RoomGroupInput[]): RoomGroupInput[] =>
  groups.map((g) => ({ ...g, label: null }));

/** Totals across every room of every family. */
export const paxFromFamilies = (
  families: readonly Family[],
): { adults: number; children: number; childrenNoBed: number; infants: number } =>
  families
    .flatMap((f) => f.rooms)
    .reduce(
      (acc, r) => ({
        adults: acc.adults + (r.adults || 0),
        children: acc.children + (r.children || 0),
        childrenNoBed: acc.childrenNoBed + Math.min(r.childrenNoBed ?? 0, r.children || 0),
        infants: acc.infants + (r.infants || 0),
      }),
      { adults: 0, children: 0, childrenNoBed: 0, infants: 0 },
    );
